import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from './LegalLayout';
import ThemeContext from '../../context/ThemeContext';

const CGVPage = () => {
  const { theme } = useContext(ThemeContext) || {};
  const isDark = theme === 'dark';

  const Section = ({ title, children }) => (
    <div className="mb-10 last:mb-0">
      <h2 className={`text-xl font-bold mb-4 flex items-center gap-3 ${
        isDark ? 'text-slate-100' : 'text-slate-900'
      }`}>
        {title}
      </h2>
      <div className={`prose max-w-none ${
        isDark ? 'prose-invert text-slate-300' : 'text-slate-600'
      }`}>
        {children}
      </div>
    </div>
  );

  return (
    <LegalLayout 
      title="Conditions Générales de Vente" 
      subtitle="Dernière mise à jour : 9 Décembre 2025"
    >
      <div className="space-y-8">
        
        <div className="p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/20 mb-8">
          <p className={`text-sm ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>
            Les présentes Conditions Générales de Vente (ci-après « CGV ») s’appliquent à toute vente de crédits, abonnements ou services associés proposés via Muzo / Muzo Studio (ci-après « le Service »).
            L’achat d’un produit ou service implique l’acceptation pleine et entière des présentes CGV.
          </p>
        </div>

        <Section title="1. Identification du vendeur">
          <p>Le Service est édité par l'Éditeur, dont les coordonnées complètes sont disponibles dans les <Link to="/legal/mentions-legales" className="text-indigo-500 hover:underline">Mentions Légales</Link>.</p>
          <p className="mt-2">Email : <a href="mailto:contact@muzo-studio.app" className="text-indigo-500 hover:underline">contact@muzo-studio.app</a></p>
          <p className="mt-2 text-sm italic">(ci-après « l’Éditeur » ou « le Vendeur »)</p>
        </Section>

        <Section title="2. Nature des services proposés">
          <p>Le Service permet :</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>l’upscale / amélioration de vidéos générées par intelligence artificielle (notamment Sora),</li>
            <li>le téléchargement de vidéos fournies par l’utilisateur ou référencées par URL,</li>
            <li>l’accès à un système de crédits,</li>
            <li>des abonnements mensuels offrant des avantages (priorité dans la queue, vitesse accrue, limites augmentées),</li>
            <li>un dashboard, un historique, et d’autres fonctionnalités numériques.</li>
          </ul>
          <p className="mt-2">
            Le Service étant purement numérique, aucune livraison physique n’est effectuée.
          </p>
        </Section>

        <Section title="3. Produits vendus">
          <h3 className="font-bold text-lg mb-2 mt-4">3.1 Packs de crédits</h3>
          <p>
            L’utilisateur peut acheter des packs de crédits permettant d’utiliser les fonctionnalités payantes du Service.
          </p>
          <p className="mt-2 font-semibold">Expiration :</p>
          <ul className="list-disc pl-5 mt-1 space-y-1">
            <li>Les crédits sont valables 1 mois à compter de la date d’achat.</li>
            <li>Passé ce délai, les crédits expirent automatiquement et ne sont ni transférables, ni réactivables.</li>
          </ul>

          <h3 className="font-bold text-lg mb-2 mt-6">3.2 Abonnements mensuels</h3>
          <p>L’utilisateur peut souscrire un abonnement mensuel donnant accès à :</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>une utilisation définie par mois,</li>
            <li>des avantages techniques (priorité, meilleure file d’attente, ressources GPU plus rapides),</li>
            <li>des conditions préférentielles selon les offres proposées sur le site.</li>
          </ul>

          <h3 className="font-bold text-lg mb-2 mt-6">3.3 Priorité / ressources dédiées</h3>
          <p>
            Certains abonnements donnent accès à une priorité de traitement ou à des capacités techniques améliorées.
          </p>
        </Section>

        <Section title="4. Prix et devise">
          <p>
            Les prix sont affichés en euros (€).
            Stripe peut afficher une conversion automatique dans d’autres devises selon le pays de l’utilisateur.
          </p>
          <p className="mt-2 font-semibold">
            Les prix sont indiqués nets, TVA non applicable – article 293B du CGI (micro-entreprise).
          </p>
        </Section>

        <Section title="5. Paiement">
          <p>
            Le paiement s’effectue exclusivement via Stripe, solution de paiement sécurisée.
            Aucun paiement n’est stocké ou traité directement par l’Éditeur.
            Les commandes ne sont considérées comme validées qu’après confirmation par Stripe.
          </p>
        </Section>

        <Section title="6. Droit de rétractation">
          <p>Conformément à l’article L221-28 du Code de la Consommation :</p>
          <p className="mt-2 italic border-l-4 border-slate-300 pl-4 py-2">
            Le droit de rétractation ne s’applique pas aux services numériques exécutés immédiatement et consommés partiellement ou totalement avant la fin du délai légal.
          </p>
          <p className="mt-2">
            En achetant un pack de crédits ou un abonnement et en cochant l’acceptation, l’utilisateur reconnaît :
          </p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>que l’exécution du service commence immédiatement,</li>
            <li>qu’il renonce expressément à son droit de rétractation.</li>
          </ul>
        </Section>

        <Section title="7. Politique de remboursement">
          <h3 className="font-bold text-lg mb-2 mt-4">7.1 Cas où un remboursement est possible</h3>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>Erreur technique avérée empêchant l’utilisation du service.</li>
            <li>Échec irréversible du traitement vidéo malgré plusieurs tentatives internes.</li>
          </ul>

          <h3 className="font-bold text-lg mb-2 mt-6">7.2 Cas où aucun remboursement n’est possible</h3>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>crédits déjà consommés (partiellement ou totalement),</li>
            <li>crédits expirés,</li>
            <li>erreurs liées au contenu fourni par l’utilisateur (lien invalide, fichier corrompu, vidéo non prise en charge),</li>
            <li>suspension du compte pour contenu illégal ou non conforme aux CGU.</li>
          </ul>

          <h3 className="font-bold text-lg mb-2 mt-6">7.3 Re-crédit automatique</h3>
          <p>
            En cas d’échec technique confirmé, les crédits consommés peuvent être automatiquement recrédités.
          </p>
        </Section>

        <Section title="8. Livraison du service">
          <p>
            Le service est fourni immédiatement après l’achat de crédits ou après activation de l’abonnement.
          </p>
          <p className="mt-2">Les délais de traitement vidéo varient selon :</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>la charge serveur,</li>
            <li>la priorité liée à l’offre choisie,</li>
            <li>la taille et la complexité de la vidéo,</li>
            <li>les performances des fournisseurs techniques (GPU, RunPod, etc.).</li>
          </ul>
          <p className="mt-2 font-semibold">
            Aucune garantie de résultat n’est fournie. La qualité de sortie dépend du modèle IA utilisé et des limitations techniques.
          </p>
        </Section>

        <Section title="9. Disponibilité des vidéos / Conservation">
          <p>
            Les vidéos sont conservées temporairement afin de permettre leur traitement et leur téléchargement.
            Aucune conservation long terme n’est garantie.
          </p>
          <p className="mt-2">
            L’utilisateur est responsable de télécharger sa vidéo finale.
            Le Service ne garantit pas que la vidéo restera accessible après traitement.
            L’Éditeur peut supprimer une vidéo à tout moment, notamment en cas de surcharge ou nécessité technique.
          </p>
        </Section>

        <Section title="10. Suspension ou suppression du compte">
          <p>
            L’Éditeur peut suspendre ou supprimer le compte d’un utilisateur, sans remboursement, notamment en cas :
          </p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>d’usage frauduleux,</li>
            <li>de contenu illégal,</li>
            <li>de violation des CGU (par ex : NSFW, mineurs, deepfakes malveillants, incitation à la haine, etc.),</li>
            <li>de tentative de contournement du service,</li>
            <li>d’utilisation de la plateforme à des fins non autorisées.</li>
          </ul>
          <p className="mt-2 font-bold text-red-500">
            En cas de suspension pour abus, aucun remboursement n’est dû.
          </p>
        </Section>

        <Section title="11. Responsabilité">
          <p>L’Éditeur n’est pas responsable :</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li>des vidéos envoyées par l’utilisateur ;</li>
            <li>de la manière dont l’utilisateur obtient ces vidéos ;</li>
            <li>des limitations techniques des modèles IA ;</li>
            <li>des dysfonctionnements liés aux prestataires externes (RunPod, CDN, Stripe, etc.) ;</li>
            <li>des pertes de données liées à une absence de téléchargement par l’utilisateur ;</li>
            <li>des usages détournés ou illégaux du service.</li>
          </ul>
          <p className="mt-4 font-semibold">Limitation</p>
          <p>
            La responsabilité totale de l’Éditeur, tous dommages confondus, est limitée au montant payé par l’utilisateur au cours des 12 derniers mois.
          </p>
        </Section>

        <Section title="12. Modification des prix et des offres">
          <p>
            L’Éditeur peut modifier à tout moment les prix, la structure des crédits, les fonctionnalités incluses dans les abonnements et les limitations techniques.
            Les modifications s’appliquent uniquement pour l’avenir.
          </p>
        </Section>

        <Section title="13. Données personnelles">
          <p>
            Les données collectées lors de l’utilisation du service sont traitées conformément à la Politique de Confidentialité (RGPD).
          </p>
          <p className="mt-2">
            Lien : <Link to="/legal/privacy" className="text-indigo-500 hover:underline">Politique de Confidentialité</Link>
          </p>
        </Section>

        <Section title="14. Loi applicable – Juridiction">
          <p>Les présentes CGV sont régies par le droit français.</p>
          <p className="mt-2">
            En cas de litige, et à défaut de résolution amiable, les tribunaux compétents seront les tribunaux français.
          </p>
          <p className="mt-2">
            Lorsque l’utilisateur réside dans l’Union européenne, il bénéficie également des protections impératives de son pays de résidence.
          </p>
        </Section>

      </div>
    </LegalLayout>
  );
};

export default CGVPage;
